import { DatePicker, Modal, Space, theme, Typography } from 'antd';
import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { catchError, EMPTY, finalize, of, switchMap, tap, throwError } from 'rxjs';
import SitesWithExistingOrdersModal from './sites-with-existing-orders.modal';
import { AuthContext } from '../../../contexts/auth-context-provider';
import { type CloneOrderResponse } from '../../../dto/CloneOrderResponse';
import ProblemDetailsDto from '../../../dto/error/problem-details-dto';
import { type ResponseDto } from '../../../dto/ResponseDto';
import { type SiteIdNameDto } from '../../../dto/SiteIdNameDto';
import { ArchivedCustomerOrder, CustomerOrder } from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import useOrderSites from '../../../hooks/use-order-sites';
import useSecuredApiV2 from '../../../hooks/use-secured-api-v2';
import { Roles } from '../../../types';
import {
  ARCHIVED_CUSTOMER_ORDER_END_POINT_URL,
  CHECK_ORDERS_FOR_SITES_ENDPOINT_URL,
  CUSTOMER_ORDER_END_POINT_URL,
} from '../../../utils/endpoints';
import { LocalDate } from '../../../utils/local-date';
import { getEarliestOrderDate } from '../../../utils/order-utils';

interface ParamsType {
  visible: boolean;
  customerOrder: CustomerOrder;
  date: LocalDate;
}

const CustomerOrderCloneModal = forwardRef(({ onSuccess }: { onSuccess: () => void }, ref) => {
  const { token } = theme.useToken();
  const notification = useNotification();
  const { session } = useContext(AuthContext);
  const [showSitesAlreadyHaveOrdersModal, setShowSitesAlreadyHaveOrdersModal] =
    useState<boolean>(false);
  const [sitesAlreadyHaveOrders, setSitesAlreadyHaveOrders] = useState<SiteIdNameDto[]>([]);
  const [params, setParams] = useState<ParamsType>({
    visible: false,
    customerOrder: new CustomerOrder(),
    date: new LocalDate(),
  });
  const [isLoading, setLoadingState] = useState(false);
  const isArchived = useMemo(
    () => params.customerOrder instanceof ArchivedCustomerOrder,
    [params.customerOrder]
  );

  // The sites of the order being cloned, only loaded while the modal is open: they are checked for
  // orders on the clone date
  const orderSites = useOrderSites(
    params.visible ? params.customerOrder.id : undefined,
    isArchived
  );
  const siteIds = useMemo(() => orderSites.data.map(({ id }) => id), [orderSites.data]);

  useEffect(() => {
    orderSites.errorMessage &&
      notification.error({
        message: 'Failed to load the order to clone',
        description: orderSites.errorMessage,
      });
  }, [orderSites.errorMessage]);

  useImperativeHandle(ref, () => ({
    updateModal: (params: ParamsType) => {
      setParams(params);
    },
  }));

  // Choose between the clone endpoint of ( customer order / archived customer order )
  const CLONE_END_POINT: string = isArchived
    ? ARCHIVED_CUSTOMER_ORDER_END_POINT_URL
    : CUSTOMER_ORDER_END_POINT_URL;

  const api = useSecuredApiV2();

  // Send clone order request to the backend
  const sendCloneOrderRequest = (): void => {
    setLoadingState(true);
    api
      .post(
        CLONE_END_POINT + `/${params.customerOrder.id}`,
        {},
        { orderDate: params.date },
        {},
        false
      )
      .pipe(
        switchMap((response) => {
          return !response.data ? throwError(() => new ProblemDetailsDto(response)) : of(response);
        }),
        tap((response: ResponseDto<CloneOrderResponse>) => {
          onSuccess();
          const impossibleToCloneMealCategories: string[] =
            response.data.impossibleToCloneMealCategories ?? [];
          const impossibleToCloneSites: string[] = response.data.impossibleToCloneSites ?? [];
          const description =
            (impossibleToCloneSites.length > 0
              ? "Couldn't clone [ " + impossibleToCloneSites.join(', ') + ' ]'
              : '') +
            (impossibleToCloneMealCategories.length > 0 && impossibleToCloneSites.length > 0
              ? ' and '
              : '') +
            (impossibleToCloneMealCategories.length > 0
              ? "Couldn't clone [ " + impossibleToCloneMealCategories.join(', ') + ' ]'
              : '');
          notification.success({
            message: 'Order cloned successfully!',
            description,
          });
          params.visible = false;
        }),
        catchError((error: ProblemDetailsDto) => {
          notification.error({
            message: 'Failed to clone order ',
            description: `${error.detail ?? ''}`,
          });
          return EMPTY;
        }),
        finalize(() => {
          setLoadingState(false);
        })
      )
      .subscribe();
  };

  // Handles the user click on the clone button in the customer order clone modal
  const onSubmit = (): void => {
    setLoadingState(true);
    // Check if sites in the customer order to be cloned, already has orders on the clone date
    api
      .post(CHECK_ORDERS_FOR_SITES_ENDPOINT_URL, { sites: siteIds }, { orderDate: params.date }, {})
      .pipe(
        switchMap((res) => {
          return res.data ? of(res.data) : throwError(() => new ProblemDetailsDto(res));
        }),
        tap((sites) => {
          if (sites.length > 0) {
            setShowSitesAlreadyHaveOrdersModal(true);
            setSitesAlreadyHaveOrders(sites);
          } else {
            sendCloneOrderRequest();
          }
        }),
        catchError((error: ProblemDetailsDto) => {
          notification.error({
            message: 'Failed to clone order',
            description: `${error.detail ?? ''}`,
          });
          return EMPTY;
        }),
        finalize(() => {
          setLoadingState(false);
        })
      )
      .subscribe();
  };

  return (
    <>
      <Modal
        title={
          <Typography.Title
            style={{ marginBlockStart: 0, marginBlockEnd: token.marginLG }}
            level={3}
          >
            Clone Customer order #{params.customerOrder.id}
          </Typography.Title>
        }
        open={params.visible}
        onOk={() => {
          onSubmit();
        }}
        onCancel={() => {
          setParams((prev): any => ({ ...prev, visible: false }));
        }}
        okText="Clone"
        okButtonProps={{
          loading: isLoading || orderSites.isLoading,
          // The sites must be known before cloning, or the check for existing orders would be skipped
          disabled: isLoading || orderSites.isLoading || !!orderSites.errorMessage,
        }}
        cancelButtonProps={{
          disabled: isLoading,
        }}
        width={500}
        forceRender
      >
        <Space direction="horizontal">
          <Typography.Text>Order date </Typography.Text>
          <DatePicker
            title={'Date Range'}
            value={params.date.toDayjs()}
            minDate={
              session?.claims.role !== Roles.ROLE_HHC_ADMIN
                ? getEarliestOrderDate(params.customerOrder.customer.customerSetting)
                : undefined
            }
            onChange={(value) => {
              setParams((prev) => ({ ...prev, date: new LocalDate(value) }));
            }}
          />
        </Space>
      </Modal>
      <SitesWithExistingOrdersModal
        isOpen={showSitesAlreadyHaveOrdersModal}
        onOk={() => {
          setShowSitesAlreadyHaveOrdersModal(false);
          sendCloneOrderRequest();
        }}
        sitesNames={sitesAlreadyHaveOrders?.map((site) => site.name).join(', ') ?? ''}
        orderDate={params.date}
        onCancel={() => {
          setShowSitesAlreadyHaveOrdersModal(false);
        }}
      ></SitesWithExistingOrdersModal>
    </>
  );
});
CustomerOrderCloneModal.displayName = 'CustomerOrderCloneModal';
export default CustomerOrderCloneModal;
